import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    getLocaleTypography,
    NCoreUIKitTheme,
    Chip,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    DocsTable,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Text";

const CUSTOM_CODE = `<Text
    customColor="english"
>
    Hello World
</Text>`;

const LOCALE_CODE = `<Text
    customLocalize={{
        activeLocale: "ar-SA"
    }}
    variant="bodyLargeSize"
>
    {arabicText}
</Text>`;

const VARIANTS = [
    "displayLargeSize",
    "displayMediumSize",
    "displaySmallSize",
    "headlineLargeSize",
    "headlineMediumSize",
    "headlineSmallSize",
    "titleLargeSize",
    "titleMediumSize",
    "titleSmallSize",
    "bodyLargeSize",
    "bodyMediumSize",
    "bodySmallSize",
    "readingLargeSize",
    "readingMediumSize",
    "readingSmallSize",
    "labelLargeSize",
    "labelMediumSize",
    "labelSmallSize",
    "monoMediumSize"
] as const;

const PLAYGROUND_VARIANTS = [
    "headlineSmallSize",
    "titleMediumSize",
    "bodyMediumSize",
    "labelMediumSize",
    "monoMediumSize"
] as const;

const PLAYGROUND_COLORS = [
    "high",
    "mid",
    "low",
    "emphasized",
    "danger"
] as const;

const COLOR_ROLES = [
    "high",
    "mid",
    "low",
    "disabled",
    "emphasized",
    "link",
    "danger",
    "success",
    "warning",
    "info"
] as const;

const CUSTOM_COLORS = [
    "english",
    "sun",
    "#7c3aed"
];

const ARABIC_LOCALE = "ar";

const ARABIC_VARIANTS = [
    "titleLargeSize",
    "bodyLargeSize",
    "bodyMediumSize",
    "readingMediumSize",
    "labelMediumSize"
] as const;

const SAMPLE_STYLE = {
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    minHeight: 48
} as const;

const ROW_STYLE = {
    justifyContent: "center",
    flexWrap: "wrap",
    display: "flex",
    gap: 8
} as const;

const getSampleKey = (variant: typeof VARIANTS[number]) => {
    if(variant.startsWith("body") || variant.startsWith("reading")) {
        return "sample.body";
    }

    if(variant.startsWith("label")) {
        return "sample.label";
    }

    if(variant.startsWith("mono")) {
        return "sample.code";
    }

    return "sample.title";
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("textPage.scaleTitle"),
        id: "scale"
    },
    {
        title: t("textPage.colorsTitle"),
        id: "colors"
    },
    {
        title: t("textPage.customColorTitle"),
        id: "custom-color"
    },
    {
        title: t("textPage.localeTitle"),
        id: "locale"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const TextPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        typography
    } = NCoreUIKitTheme.useContext();

    const [
        variant,
        setVariant
    ] = useState<typeof PLAYGROUND_VARIANTS[number]>("bodyMediumSize");

    const [
        color,
        setColor
    ] = useState<typeof PLAYGROUND_COLORS[number]>("mid");

    const arabicTypography = getLocaleTypography({
        locale: ARABIC_LOCALE,
        typography
    });

    const code = (value: string) => <InlineCode>
        {value}
    </InlineCode>;

    const getMetrics = (token: NCoreUIKit.TypographyToken) => `${token.fontSize} / ${token.lineHeight}`;

    const usageCode = `<Text
    variant="${variant}"
    color="${color}"
>
    Hello World
</Text>`;

    return <Article>
        <ComponentHeader
            lead={t("textPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("textPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("textPage.overviewCaption")}
                isStacked={true}
            >
                <div
                    style={SAMPLE_STYLE}
                >
                    <Text
                        variant={variant}
                        color={color}
                    >
                        {t(getSampleKey(variant))}
                    </Text>
                </div>
                <div
                    style={ROW_STYLE}
                >
                    {
                        PLAYGROUND_VARIANTS.map((item) => <Chip
                            onPress={() => setVariant(item)}
                            isSelected={item === variant}
                            spreadBehaviour="free"
                            isClosable={false}
                            size="small"
                            title={item}
                            key={item}
                        />)
                    }
                </div>
                <div
                    style={ROW_STYLE}
                >
                    {
                        PLAYGROUND_COLORS.map((item) => <Chip
                            onPress={() => setColor(item)}
                            isSelected={item === color}
                            spreadBehaviour="free"
                            isClosable={false}
                            size="small"
                            title={item}
                            key={item}
                        />)
                    }
                </div>
            </Preview>
            <CodeBlock
                code={usageCode}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("textPage.scaleTitle")}
            id="scale"
        >
            <Paragraph>
                {t("textPage.scaleText")}
            </Paragraph>
            <DocsTable
                rows={VARIANTS.map((variant) => [
                    code(variant),
                    <Text
                        variant={variant}
                        color="high"
                    >
                        {t(getSampleKey(variant))}
                    </Text>,
                    getMetrics(typography[variant]),
                    typography[variant].fontWeight
                ])}
                headers={[
                    t("textPage.columnVariant"),
                    t("textPage.columnSample"),
                    t("textPage.columnSize"),
                    t("textPage.columnWeight")
                ]}
            />
        </Section>
        <Section
            title={t("textPage.colorsTitle")}
            id="colors"
        >
            <Paragraph>
                {t("textPage.colorsText")}
            </Paragraph>
            <Preview>
                {
                    COLOR_ROLES.map((color) => <Text
                        variant="titleMediumSize"
                        color={color}
                        key={color}
                    >
                        {color}
                    </Text>)
                }
            </Preview>
        </Section>
        <Section
            title={t("textPage.customColorTitle")}
            id="custom-color"
        >
            <Paragraph>
                {t("textPage.customColorText")}
            </Paragraph>
            <Preview>
                {
                    CUSTOM_COLORS.map((customColor) => <Text
                        variant="titleMediumSize"
                        customColor={customColor}
                        key={customColor}
                    >
                        {customColor}
                    </Text>)
                }
            </Preview>
            <CodeBlock
                code={CUSTOM_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("textPage.localeTitle")}
            id="locale"
        >
            <Paragraph>
                {t("textPage.localeText")}
            </Paragraph>
            <DocsTable
                rows={ARABIC_VARIANTS.map((variant) => [
                    code(variant),
                    getMetrics(typography[variant]),
                    getMetrics(arabicTypography[variant])
                ])}
                headers={[
                    t("textPage.columnVariant"),
                    t("textPage.columnLatin"),
                    t("textPage.columnArabic")
                ]}
            />
            <CodeBlock
                code={LOCALE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("textPage.a11yPassThrough"),
                    t("textPage.a11yHeading"),
                    t("textPage.a11yFocus"),
                    t("textPage.a11yTheme"),
                    t("textPage.a11yArabic")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("textPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        description: t("textPage.propVariant"),
                        defaultValue: "\"bodyMediumSize\"",
                        type: "keyof Typography",
                        name: "variant"
                    },
                    {
                        description: t("textPage.propColor"),
                        type: "keyof TextContentColors",
                        defaultValue: "\"mid\"",
                        name: "color"
                    },
                    {
                        description: t("textPage.propCustomColor"),
                        type: "keyof ProjectColorPalette | string",
                        name: "customColor"
                    },
                    {
                        description: t("textPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("textPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("textPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default TextPage;
